// Shopping Lists (Einkaufszettel) API - Real Backend Implementation
// Connects to Spring Boot backend at /api/shopping-lists

// Types (JSDoc for better DX)
/**
 * @typedef {Object} ShoppingListItem
 * @property {string} name - Item name (e.g. "Hähnchenfilet", "Tomaten")
 * @property {number} quantity - Quantity amount (e.g. 300, 2)
 * @property {string} unit - Unit of measurement (e.g. "g", "Stück", "ml", "TL", "EL")
 * @property {string=} note - Optional note (e.g. "mittelgroß", "Bio")
 * @property {string[]=} alternatives - Optional alternative ingredients
 * @property {number=} originalQuantity - Original quantity before scaling (for recipe-based lists)
 */

/**
 * @typedef {Object} ShoppingList
 * @property {string} id - Unique identifier
 * @property {string} name - List name
 * @property {string=} description - Optional description for manual lists
 * @property {boolean} isManual - True if manually created, false if recipe-based
 * @property {string=} recipeId - Associated recipe ID (null for manual lists)
 * @property {string=} recipeTitle - Associated recipe title (null for manual lists)
 * @property {string} createdAt - ISO 8601 timestamp
 * @property {string=} updatedAt - ISO 8601 timestamp of last update
 * @property {number=} targetServings - Target servings for recipe-based lists (null for manual)
 * @property {ShoppingListItem[]} items - Array of shopping items
 * @property {number[]} checkedItems - Array of checked item indices
 * @property {number} progress - Completion progress in percent (0-100)
 * @property {string=} userId - User ID who created this list
 */

/**
 * @typedef {Object} CreateShoppingListPayload
 * @property {string} name - List name (required)
 * @property {string=} description - Optional description
 * @property {boolean=} isManual - Default: true
 * @property {string=} recipeId - Recipe ID if recipe-based
 * @property {string=} recipeTitle - Recipe title if recipe-based
 * @property {number=} targetServings - Target servings for recipe-based lists
 * @property {ShoppingListItem[]=} items - Initial items (empty array if not provided)
 */

/**
 * @typedef {Object} UpdateShoppingListPayload
 * @property {string=} name - Updated list name
 * @property {string=} description - Updated description
 * @property {ShoppingListItem[]=} items - Updated items array
 * @property {number[]=} checkedItems - Updated checked items indices
 */

/**
 * @typedef {"all"|"open"|"completed"|"manual"|"recipe"|"shared"} FilterType
 */

/**
 * @typedef {Object} ShareInfo
 * @property {boolean} valid - Whether the share is valid
 * @property {string=} listName - Name of the shared list
 * @property {string=} ownerName - Name of the list owner
 * @property {number=} itemCount - Number of items in the list
 * @property {string=} expiresAt - Expiration timestamp
 * @property {string=} message - Error message if invalid
 */

/**
 * @typedef {Object} ShareDto
 * @property {number} id - Share ID
 * @property {string} shareToken - The share token
 * @property {string} shareUrl - Full share URL
 * @property {number} shoppingListId - Shopping list ID
 * @property {string} shoppingListName - Shopping list name
 * @property {string} createdAt - Creation timestamp
 * @property {string=} expiresAt - Expiration timestamp
 * @property {boolean} active - Whether share is active
 * @property {number} maxUses - Max uses (0 = unlimited)
 * @property {number} currentUses - Current use count
 */

/**
 * @typedef {Object} JoinShareResponse
 * @property {boolean} success - Whether join was successful
 * @property {string} message - Response message
 * @property {ShoppingList=} shoppingList - The joined list
 * @property {boolean=} alreadyMember - True if already a member
 */

/**
 * @typedef {Object} ListQueryParams
 * @property {FilterType=} filter - Filter type (default: "all")
 * @property {string=} q - Search query (searches name, description, recipeTitle)
 * @property {string=} sortBy - Sort field: "createdAt"|"updatedAt"|"name"|"progress" (default: "createdAt")
 * @property {string=} sortOrder - Sort order: "asc"|"desc" (default: "desc")
 * @property {number=} limit - Max number of results
 * @property {number=} offset - Pagination offset
 */

// API Base URL - uses environment variable or defaults to /api
const getBaseUrl = () => {
  return process.env.REACT_APP_API_URL || '/api';
};

// Get auth token from localStorage
const getAuthToken = () => {
  const token = localStorage.getItem('authToken');
  return token;
};

// Build headers with auth token
const getHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Generic fetch wrapper with error handling
async function apiFetch(endpoint, options = {}) {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: getHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new Error('Nicht autorisiert. Bitte erneut anmelden.');
    }
    if (response.status === 404) {
      throw new Error('Einkaufszettel nicht gefunden.');
    }
    const errorText = await response.text();
    throw new Error(errorText || `HTTP Error: ${response.status}`);
  }

  // Handle empty responses (e.g., DELETE)
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return { success: true };
}

/**
 * Shopping List API
 *
 * REST Endpoints (Spring Boot Backend):
 * - GET    /api/shopping-lists              -> list()
 * - POST   /api/shopping-lists              -> create()
 * - GET    /api/shopping-lists/{id}         -> getById()
 * - PUT    /api/shopping-lists/{id}         -> update()
 * - DELETE /api/shopping-lists/{id}         -> remove()
 * - POST   /api/shopping-lists/{id}/items   -> addItem()
 * - DELETE /api/shopping-lists/{id}/items/{itemIndex}  -> removeItem()
 * - PUT    /api/shopping-lists/{id}/items/{itemIndex}/toggle  -> toggleItemChecked()
 * - POST   /api/shopping-lists/{id}/duplicate  -> duplicate()
 * - GET    /api/shopping-lists/stats         -> getStats()
 * - DELETE /api/shopping-lists/bulk          -> bulkDelete()
 */
export const einkaufszettelApi = {
  /**
   * List shopping lists with optional filters, search, and sorting
   * @param {ListQueryParams} params - Query parameters
   * @returns {Promise<ShoppingList[]>}
   */
  async list(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.filter) queryParams.append('filter', params.filter);
    if (params.q) queryParams.append('q', params.q);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const queryString = queryParams.toString();
    const endpoint = `/shopping-lists${queryString ? `?${queryString}` : ''}`;

    return apiFetch(endpoint);
  },

  /**
   * Get a single shopping list by ID
   * @param {string} id - List ID
   * @returns {Promise<ShoppingList>}
   */
  async getById(id) {
    return apiFetch(`/shopping-lists/${id}`);
  },

  /**
   * Create a new shopping list (manual or recipe-based)
   * @param {CreateShoppingListPayload} payload
   * @returns {Promise<ShoppingList>}
   */
  async create(payload) {
    return apiFetch('/shopping-lists', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Update an existing shopping list
   * @param {string} id - List ID
   * @param {UpdateShoppingListPayload} payload
   * @returns {Promise<ShoppingList>}
   */
  async update(id, payload) {
    return apiFetch(`/shopping-lists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Delete a shopping list
   * @param {string} id - List ID
   * @returns {Promise<{success: boolean}>}
   */
  async remove(id) {
    return apiFetch(`/shopping-lists/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Add an item to a shopping list (manual lists only)
   * @param {string} id - List ID
   * @param {ShoppingListItem} item - Item to add
   * @param {number} [position] - Optional position to insert item (0 = beginning)
   * @returns {Promise<ShoppingList>}
   */
  async addItem(id, item, position = 0) {
    return apiFetch(`/shopping-lists/${id}/items`, {
      method: 'POST',
      body: JSON.stringify({ ...item, position }),
    });
  },

  /**
   * Remove an item from a shopping list (manual lists only)
   * @param {string} id - List ID
   * @param {number} itemIndex - Item index to remove
   * @returns {Promise<ShoppingList>}
   */
  async removeItem(id, itemIndex) {
    return apiFetch(`/shopping-lists/${id}/items/${itemIndex}`, {
      method: 'DELETE',
    });
  },

  /**
   * Toggle an item's checked status
   * @param {string} id - List ID
   * @param {number} itemIndex - Item index to toggle
   * @returns {Promise<ShoppingList>}
   */
  async toggleItemChecked(id, itemIndex) {
    return apiFetch(`/shopping-lists/${id}/items/${itemIndex}/toggle`, {
      method: 'PUT',
    });
  },

  /**
   * Duplicate a shopping list (creates a copy)
   * @param {string} id - List ID to duplicate
   * @returns {Promise<ShoppingList>}
   */
  async duplicate(id) {
    return apiFetch(`/shopping-lists/${id}/duplicate`, {
      method: 'POST',
    });
  },

  /**
   * Get statistics about shopping lists
   * @returns {Promise<Object>} Statistics object
   */
  async getStats() {
    return apiFetch('/shopping-lists/stats');
  },

  /**
   * Bulk delete shopping lists
   * @param {string[]} ids - Array of list IDs to delete
   * @returns {Promise<{deleted: number}>}
   */
  async bulkDelete(ids) {
    return apiFetch('/shopping-lists/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
  },

  // ===== Share Endpoints =====

  /**
   * Create a share link for a shopping list
   * @param {string} listId - List ID to share
   * @param {Object} options - Share options
   * @param {number=} options.expiryHours - Hours until expiration (default: 24)
   * @param {number=} options.maxUses - Max uses (0 = unlimited)
   * @returns {Promise<ShareDto>}
   */
  async createShare(listId, options = {}) {
    return apiFetch(`/shopping-lists/${listId}/share`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  },

  /**
   * Get all active shares for a shopping list
   * @param {string} listId - List ID
   * @returns {Promise<ShareDto[]>}
   */
  async getActiveShares(listId) {
    return apiFetch(`/shopping-lists/${listId}/shares`);
  },

  /**
   * Revoke a share link
   * @param {string} token - Share token to revoke
   * @returns {Promise<{success: boolean}>}
   */
  async revokeShare(token) {
    return apiFetch(`/shopping-lists/share/${token}`, {
      method: 'DELETE',
    });
  },

  /**
   * Join a shopping list via share token
   * @param {string} token - Share token
   * @returns {Promise<JoinShareResponse>}
   */
  async joinByToken(token) {
    return apiFetch(`/shopping-lists/join/${token}`, {
      method: 'POST',
    });
  },

  /**
   * Get public share info (no auth required)
   * @param {string} token - Share token
   * @returns {Promise<ShareInfo>}
   */
  async getShareInfo(token) {
    return apiFetch(`/shopping-lists/share/${token}/info`);
  },

  /**
   * Remove a collaborator from a list (owner only)
   * @param {string} listId - List ID
   * @param {string} collaboratorId - User ID to remove
   * @returns {Promise<{success: boolean}>}
   */
  async removeCollaborator(listId, collaboratorId) {
    return apiFetch(`/shopping-lists/${listId}/collaborators/${collaboratorId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Leave a shared list (as collaborator)
   * @param {string} listId - List ID to leave
   * @returns {Promise<{success: boolean}>}
   */
  async leaveList(listId) {
    return apiFetch(`/shopping-lists/${listId}/leave`, {
      method: 'POST',
    });
  }
};

export default einkaufszettelApi;
